import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  MainProfileContainer,
  SubmitContainer,
} from 'pages/addprofile/Profile.style';
import { Notification } from 'components/CustomNotification';
import { BrandIconSpinner, Button } from 'components';
import {
  getProfileByuserId,
  getProfileByuserName,
  updateProfileList,
} from 'redux/features/ProfileSlice';
import UploadImagePreview from 'components/UploadImagePreview';
import ImageCrop from 'components/ImageCrop';

const Profile = ({ onClose, typeInfo, profileImages }) => {
  const cropperRef = useRef();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getProfile } = useSelector((state) => state?.ProfileSlice);
  const userId = getProfile?.userId;

  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [postDetails, setPostDetails] = useState({ image: [] });
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const defaultImage =
      typeInfo === 'profile'
        ? profileImages?.coverImage
        : profileImages?.profileImage;

    setPostDetails({ image: defaultImage ? [defaultImage] : [] });
  }, [typeInfo, profileImages]);

  const handleReset = () => {
    setPostDetails({ image: [] });
    onClose();
  };

  const handleImageChange = useCallback(
    (data, name) => {
      if (data.length > 1) {
        Notification('warning', "You can't upload more than one photo");
        data = [data[0]];
      }

      setPostDetails((prev) => ({ ...prev, [name]: data }));

      // if existing image was a URL, store for tracking
      if (typeof postDetails.image?.[0] === 'string') {
        setAttachment(postDetails.image?.[0]);
      }
    },
    [postDetails]
  );

  const handleSubmit = useCallback(async () => {
    const croppedResult = await cropperRef.current?.getCroppedImage();
    const formData = new FormData();

    if (typeInfo === 'profile') {
      const coverFile = postDetails.image[0];
      if (coverFile) {
        formData.append('coverImage', coverFile);
      } else {
        formData.append('coverImage', '');
      }
    } else {
      if (croppedResult?.blob) {
        formData.append(
          'profileImage',
          croppedResult.blob,
          `${getProfile?.displayName}.jpg`
        );
      } else {
        formData.append('profileImage', '');
      }
    }

    if (attachment) {
      formData.append('attachment', JSON.stringify([attachment]));
    }

    setLoading(true);

    dispatch(updateProfileList(formData, userId))
      .then(() => {
        Notification(
          'success',
          typeInfo === 'profile'
            ? 'Cover Image updated successfully'
            : croppedResult?.blob ? 'Profile Image updated successfully' : 'Profile Image deleted successfully' 
        );
        dispatch(getProfileByuserId(userId));
        dispatch(getProfileByuserName());
        handleReset();
      })
      .catch((err) => {
        Notification(
          'warning',
          err?.response?.data?.message || 'Update failed'
        );
        setLoading(false);
      });
  }, [postDetails.image, attachment, typeInfo, userId]);

  const isCoverImageReady = postDetails.image?.[0] !== getProfile.coverImage;

  const isProfileImageReady = previewImage?.startsWith('blob:') || postDetails.image?.[0] !== getProfile.profileImage;

  const isSaveEnabled = typeInfo === 'profile' ? isCoverImageReady : isProfileImageReady;

  return (
    <>
      <BrandIconSpinner loading={loading} />
      <MainProfileContainer>
        {typeInfo !== 'profile' ? (
          <ImageCrop
            ref={cropperRef}
            profileImage={postDetails.image}
            handleSetImage={setPreviewImage}
            handleDeleteImg={() =>
              setPostDetails((prev) => ({ ...prev, image: [] }))
            }
          />
        ) : (
          <UploadImagePreview
            isReplace
            isDelete
            maxFiles={1}
            types="imageupload"
            value={postDetails.image}
            handleFileSRC={(data) => handleImageChange(data, 'image')}
            onCloseFiles={(url) => {
              if (url?.startsWith('https://')) {
                setAttachment(url);
              }
              setPostDetails((prev) => ({ ...prev, image: [] }));
            }}
          />
        )}
      </MainProfileContainer>

      <SubmitContainer className="modal-footer">
        <Button
          label={t('profile.Save')}
          disabled={!isSaveEnabled}
          onClick={handleSubmit}
          type="text"
        />
        <Button label={t('profile.Close')} onClick={handleReset} type="text" />
      </SubmitContainer>
    </>
  );
};

export default Profile;
